import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown, Copy, ExternalLink, Link2, RotateCcw, Star } from 'lucide-react';
import { MODULES } from '../../config/moduleDefs';
import { SERVICE_EXTERNAL_SECTION_ORDER, checkLocalFolderLauncher, getAvailableExternalLinkSections, getServiceScopedLinks, isServiceExternalSectionHidden, isServiceSectionDefaultOpen, pollForLauncher, restartLocalFolderLauncher, tryLaunchLocalFolderPath } from '../../config/moduleHelpers';
import { supabase } from '../../services/supabase';

const FAVORITES_STORAGE_KEY = 'staffhub.quicklinks.favorite-destinations';

const ExternalQuickLinksButton = ({ config, record, compact = false, className = '' }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [sourceLinkData, setSourceLinkData] = useState(null);
    const [copiedSectionKey, setCopiedSectionKey] = useState('');
    const [copiedLinkKey, setCopiedLinkKey] = useState('');
    const [popupBlockedCount, setPopupBlockedCount] = useState(0);
    const [localLaunchNotice, setLocalLaunchNotice] = useState('');
    const [launcherStatus, setLauncherStatus] = useState('unknown');
    const [isRestartingLauncher, setIsRestartingLauncher] = useState(false);
    const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
    const [favoriteKeys, setFavoriteKeys] = useState(() => {
        try {
            const saved = window.localStorage.getItem(FAVORITES_STORAGE_KEY);
            const parsed = saved ? JSON.parse(saved) : [];
            return Array.isArray(parsed) ? parsed : [];
        } catch {
            return [];
        }
    });
    const containerRef = useRef(null);
    const triggerRef = useRef(null);
    const menuRef = useRef(null);

    const sourceTypeKey = String(record?.source_type || '').trim().toLowerCase();
    const isServiceModule = Boolean(config?.enrollMode && sourceTypeKey);
    const sourceModuleMap = {
        company: MODULES.COMPANY,
        charity: MODULES.CHARITY,
        individual: MODULES.INDIVIDUAL,
        unregistered: MODULES.UNREGISTERED,
        partnership: MODULES.PARTNERSHIP,
    };
    const sourceConfig = sourceModuleMap[sourceTypeKey] || null;
    const effectiveSectionConfig = sourceConfig || config;
    const sourceTableByType = {
        company: 'clients_coy',
        charity: 'clients_charity',
        individual: 'clients_individual',
        unregistered: 'clients_unregistered',
        partnership: 'clients_partnership',
    };

    const isSectionDefaultOpen = (sectionLabel) => {
        if (isServiceModule) return isServiceSectionDefaultOpen(config?.id, sectionLabel);
        return sectionLabel === 'Client Folders';
    };

    useEffect(() => {
        if (!isServiceModule || !sourceTypeKey || !record?.source_id) {
            setSourceLinkData(null);
            return undefined;
        }

        const sourceTable = sourceTableByType[sourceTypeKey];
        if (!sourceTable) {
            setSourceLinkData(null);
            return undefined;
        }

        let active = true;

        (async () => {
            const { data } = await supabase
                .from(sourceTable)
                .select('*')
                .eq('id', record.source_id)
                .maybeSingle();

            if (active) setSourceLinkData(data || null);
        })();

        return () => {
            active = false;
        };
    }, [isServiceModule, sourceTypeKey, record?.source_id]);

    const linkContextData = useMemo(() => {
        if (!sourceLinkData) return record;

        const merged = { ...sourceLinkData };
        Object.entries(record || {}).forEach(([key, value]) => {
            const isEmptyString = typeof value === 'string' && value.trim() === '';
            const isNullish = value === null || value === undefined;

            if (isNullish || isEmptyString) return;
            if (isServiceModule && key.startsWith('svc_') && sourceLinkData[key] !== undefined) return;

            merged[key] = value;
        });

        return merged;
    }, [isServiceModule, record, sourceLinkData]);

    const sections = useMemo(() => {
        const baseSections = getAvailableExternalLinkSections(effectiveSectionConfig, linkContextData)
            .sort((left, right) => {
                const leftKey = left?.sectionLabel || left?.providerLabel || '';
                const rightKey = right?.sectionLabel || right?.providerLabel || '';
                const leftOrder = SERVICE_EXTERNAL_SECTION_ORDER[leftKey] ?? 99;
                const rightOrder = SERVICE_EXTERNAL_SECTION_ORDER[rightKey] ?? 99;

                if (leftOrder !== rightOrder) return leftOrder - rightOrder;
                return leftKey.localeCompare(rightKey);
            })
            .filter((section) => {
                if (!isServiceModule) return true;
                const sectionLabel = section?.sectionLabel || section?.providerLabel || '';
                return !isServiceExternalSectionHidden(sectionLabel);
            });

        return baseSections
            .map((section) => {
                const sectionLabel = section.providerLabel || section.sectionLabel || 'Section';
                const links = isServiceModule
                    ? getServiceScopedLinks(config?.id, sectionLabel, [...(section.links || [])])
                    : [...(section.links || [])];

                if (links.length === 0) return null;

                const decoratedLinks = links
                .map((link, index) => {
                    const favoriteKey = `${config?.id || 'module'}::${sectionLabel || 'section'}::${link.key}`;
                    return {
                        ...link,
                        favoriteKey,
                        isFavorite: favoriteKeys.includes(favoriteKey),
                        originalIndex: index,
                    };
                })
                .sort((a, b) => {
                    if (a.isFavorite !== b.isFavorite) return a.isFavorite ? -1 : 1;
                    return a.originalIndex - b.originalIndex;
                });

                return {
                    ...section,
                    links: decoratedLinks,
                };
            })
            .filter(Boolean);
    }, [config?.id, effectiveSectionConfig, favoriteKeys, isServiceModule, linkContextData]);
    const hasLocalFolderLinks = useMemo(
        () => sections.some((section) =>
            section.links.some((link) => /^(file:\/\/\/|ms-explorer:)/i.test(String(link.url || '')))
        ),
        [sections]
    );

    useEffect(() => {
        if (!isOpen) return undefined;

        const handleOutsideClick = (event) => {
            const isInsideTrigger = containerRef.current?.contains(event.target);
            const isInsideMenu = menuRef.current?.contains(event.target);

            if (!isInsideTrigger && !isInsideMenu) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleOutsideClick);
        return () => document.removeEventListener('mousedown', handleOutsideClick);
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return undefined;

        const updateMenuPosition = () => {
            const rect = triggerRef.current?.getBoundingClientRect();
            if (!rect) return;

            const menuWidth = 288;
            const viewportPadding = 8;
            const nextLeft = Math.min(
                Math.max(viewportPadding, rect.right - menuWidth),
                window.innerWidth - menuWidth - viewportPadding
            );

            setMenuPosition({
                top: rect.bottom + 8,
                left: nextLeft,
            });
        };

        updateMenuPosition();

        window.addEventListener('resize', updateMenuPosition);
        window.addEventListener('scroll', updateMenuPosition, true);

        return () => {
            window.removeEventListener('resize', updateMenuPosition);
            window.removeEventListener('scroll', updateMenuPosition, true);
        };
    }, [isOpen]);

    useEffect(() => {
        if (!copiedSectionKey) return undefined;

        const timeoutId = window.setTimeout(() => setCopiedSectionKey(''), 1400);
        return () => window.clearTimeout(timeoutId);
    }, [copiedSectionKey]);

    useEffect(() => {
        if (!copiedLinkKey) return undefined;

        const timeoutId = window.setTimeout(() => setCopiedLinkKey(''), 1400);
        return () => window.clearTimeout(timeoutId);
    }, [copiedLinkKey]);

    useEffect(() => {
        if (!localLaunchNotice) return undefined;

        const timeoutId = window.setTimeout(() => setLocalLaunchNotice(''), 2600);
        return () => window.clearTimeout(timeoutId);
    }, [localLaunchNotice]);

    useEffect(() => {
        if (!isOpen || !hasLocalFolderLinks) return undefined;

        let active = true;
        setLauncherStatus('checking');

        checkLocalFolderLauncher().then((isOnline) => {
            if (active) {
                setLauncherStatus(isOnline ? 'online' : 'offline');
            }
        });

        return () => {
            active = false;
        };
    }, [hasLocalFolderLinks, isOpen]);

    useEffect(() => {
        try {
            window.localStorage.setItem(FAVORITES_STORAGE_KEY, JSON.stringify(favoriteKeys));
        } catch {
            // Ignore persistence failures
        }
    }, [favoriteKeys]);

    if (sections.length === 0) return null;

    const totalLinks = sections.reduce((sum, section) => sum + section.links.length, 0);
    const pinnedLinks = sections.flatMap((section) =>
        section.links
            .filter((link) => link.isFavorite)
            .map((link) => ({
                ...link,
                sectionLabel: section.providerLabel || section.sectionLabel || 'Section',
            }))
    );

    const handleCloseMenu = () => {
        setIsOpen(false);
        setPopupBlockedCount(0);
        triggerRef.current?.focus();
    };

    const handleOpenPinnedLinks = () => {
        if (pinnedLinks.length === 0) return;

        // window.open() returns null when the browser's popup blocker prevents the
        // new tab from opening. We count how many were blocked and surface an inline
        // message so the user knows to allow popups for this site once — after that
        // every subsequent click will open all tabs successfully.
        let blocked = 0;
        for (const link of pinnedLinks) {
            const win = window.open(link.url, '_blank');
            if (!win) blocked++;
        }

        if (blocked > 0) {
            // Keep the menu open so the warning is visible
            setPopupBlockedCount(blocked);
        } else {
            handleCloseMenu();
        }
    };

    const copyText = async (valueToCopy, fallbackLabel) => {
        try {
            if (navigator?.clipboard?.writeText) {
                await navigator.clipboard.writeText(valueToCopy);
            } else {
                const textArea = document.createElement('textarea');
                textArea.value = valueToCopy;
                textArea.setAttribute('readonly', '');
                textArea.style.position = 'absolute';
                textArea.style.left = '-9999px';
                document.body.appendChild(textArea);
                textArea.select();
                document.execCommand('copy');
                document.body.removeChild(textArea);
            }

            return true;
        } catch {
            window.prompt(`Copy this ${fallbackLabel}:`, valueToCopy);
            return false;
        }
    };

    const handleCopyReference = async (section) => {
        const valueToCopy = section?.referenceValue || '';
        if (!valueToCopy) return;

        if (await copyText(valueToCopy, 'value')) {
            setCopiedSectionKey(section.providerLabel || section.sectionLabel || section.referenceField || 'copied');
        }
    };

    const handleCopyLink = async (section, link) => {
        if (!link?.url) return;

        const isLocalLink = /^(file:\/\/\/|ms-explorer:)/i.test(String(link.url || ''));
        const valueToCopy = isLocalLink
            ? (link?.localPath || String(link?.url || '').replace(/^ms-explorer:/i, ''))
            : link.url;

        if (await copyText(valueToCopy, isLocalLink ? 'folder path' : 'link')) {
            setCopiedLinkKey(`${section.providerLabel || section.sectionLabel || 'section'}-${link.key}`);
        }
    };

    const handleRestartLauncher = async () => {
        setIsRestartingLauncher(true);
        const restarted = await restartLocalFolderLauncher();

        if (!restarted) {
            await copyText("Start-ScheduledTask -TaskName 'StaffHub Folder Launcher'", 'launcher restart command');
            setLauncherStatus('offline');
            setLocalLaunchNotice('Could not restart automatically. Restart command copied for admin terminal.');
            setIsRestartingLauncher(false);
            return;
        }

        setLocalLaunchNotice('Launcher restarting — checking status…');
        const isOnline = await pollForLauncher(14000, 1500);
        setLauncherStatus(isOnline ? 'online' : 'offline');
        setLocalLaunchNotice(isOnline ? 'Launcher restarted successfully.' : 'Restart requested. Please retry in a moment.');
        setIsRestartingLauncher(false);
    };

    const handleLocalLinkClick = async (event, section, link) => {
        event.preventDefault();

        const localPath = link?.localPath || String(link?.url || '').replace(/^ms-explorer:/i, '');
        const launchedViaHelper = await tryLaunchLocalFolderPath(localPath);

        if (launchedViaHelper) {
            setLauncherStatus('online');
            setLocalLaunchNotice('Opened local folder via StaffHub helper.');
            handleCloseMenu();
            return;
        }

        setLauncherStatus('offline');

        try {
            window.location.href = link.url;
        } catch {
            // ignore launch failures; clipboard fallback still applies
        }

        const copied = await copyText(localPath || link.url, 'folder path');

        if (copied) {
            setCopiedLinkKey(`${section.providerLabel || section.sectionLabel || 'section'}-${link.key}`);
        }

        setLocalLaunchNotice('Attempted folder launch. Path copied — paste into File Explorer if blocked.');
    };

    const toggleFavorite = (favoriteKey) => {
        setFavoriteKeys((current) => (
            current.includes(favoriteKey)
                ? current.filter((key) => key !== favoriteKey)
                : [...current, favoriteKey]
        ));
    };

    const menuContent = (
        <div
            ref={menuRef}
            className="w-72 rounded-xl border border-slate-200 bg-white shadow-xl z-[11000] p-2"
            style={{ position: 'fixed', top: `${menuPosition.top}px`, left: `${menuPosition.left}px` }}
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => {
                if (event.key === 'Escape') {
                    event.preventDefault();
                    handleCloseMenu();
                }
            }}
        >
            {localLaunchNotice && (
                <div className="mb-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-800 leading-snug">
                    {localLaunchNotice}
                </div>
            )}

            {hasLocalFolderLinks && (
                <div className={`mb-2 rounded-lg border px-3 py-2 text-[11px] leading-snug ${launcherStatus === 'online' ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : launcherStatus === 'checking' ? 'border-slate-200 bg-slate-50 text-slate-700' : 'border-amber-200 bg-amber-50 text-amber-800'}`}>
                    <div className="flex items-center justify-between gap-2">
                        <span>
                            {launcherStatus === 'online'
                                ? 'Launcher Status: Connected (one-click local folder open enabled)'
                                : launcherStatus === 'checking'
                                    ? 'Launcher Status: Checking...'
                                    : 'Launcher Status: Not running (click will copy folder path fallback).'}
                        </span>
                        <button
                            type="button"
                            onClick={handleRestartLauncher}
                            disabled={isRestartingLauncher}
                            className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white/80 px-2 py-1 text-[10px] font-semibold text-slate-700 hover:bg-white disabled:opacity-50"
                            title="Restart local launcher"
                        >
                            <RotateCcw className={`w-3 h-3 ${isRestartingLauncher ? 'animate-spin' : ''}`} />
                            {isRestartingLauncher ? 'Restarting...' : 'Restart'}
                        </button>
                    </div>
                </div>
            )}

            {pinnedLinks.length > 0 && (
                <div className="mb-2 border-b border-slate-100 pb-2">
                    {popupBlockedCount > 0 && (
                        <div className="mb-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-[11px] text-rose-700 leading-snug">
                            <strong>{popupBlockedCount} link{popupBlockedCount !== 1 ? 's' : ''} blocked by your browser.</strong>
                            {' '}Click the popup icon in your address bar and choose <em>Always allow</em>, then click the button again.
                        </div>
                    )}
                    <button
                        type="button"
                        onClick={handleOpenPinnedLinks}
                        className="flex w-full items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800 hover:bg-amber-100 transition-colors"
                        title={`Open ${pinnedLinks.length} pinned link${pinnedLinks.length === 1 ? '' : 's'}`}
                    >
                        <span className="flex items-center gap-2">
                            <Star className="w-3.5 h-3.5 fill-current" />
                            <span>Open Pinned Links</span>
                        </span>
                        <span className="text-[10px] font-bold uppercase tracking-wide text-amber-700">
                            {pinnedLinks.length}
                        </span>
                    </button>
                </div>
            )}

            {sections.map((section) => {
                const sectionLabel = section.providerLabel || section.sectionLabel || 'Section';
                return (
                <details
                    key={section.sectionLabel || section.providerLabel}
                    className="mb-2 rounded-lg border border-slate-200 bg-white group last:mb-0"
                    {...(isSectionDefaultOpen(sectionLabel) ? { open: true } : {})}
                >
                    <summary className="list-none cursor-pointer select-none px-2.5 py-2 flex items-center justify-between gap-2 hover:bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-2 min-w-0">
                            <div className="text-[10px] font-bold uppercase tracking-wide text-slate-500 truncate">
                                {sectionLabel}
                            </div>
                            <span className="rounded-full border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[9px] font-bold text-slate-500">
                                {section.links.length}
                            </span>
                        </div>
                        <ChevronDown className="w-3.5 h-3.5 text-slate-400 transition-transform group-open:rotate-180" />
                    </summary>

                    <div className="px-2 pb-2 border-t border-slate-100">
                        <div className="flex items-center justify-between gap-2 px-0.5 py-1.5">
                            <button
                                type="button"
                                onClick={() => handleCopyReference(section)}
                                className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-slate-50 px-2 py-1 text-[10px] font-semibold text-slate-600 hover:bg-slate-100 hover:text-slate-800 transition-colors"
                                title={`Copy ${section.referenceLabel || 'reference'}`}
                            >
                                {copiedSectionKey === (section.providerLabel || section.sectionLabel || section.referenceField)
                                    ? <Check className="w-3 h-3 text-emerald-600" />
                                    : <Copy className="w-3 h-3" />}
                                <span>
                                    {copiedSectionKey === (section.providerLabel || section.sectionLabel || section.referenceField)
                                        ? 'Copied'
                                        : `Copy ${section.referenceLabel || 'ID'}`}
                                </span>
                            </button>
                        </div>
                        <div className="px-1 pb-1 text-[11px] text-slate-500 font-mono truncate">
                            {section.referenceValue}
                        </div>

                        <div className="space-y-1">
                            {section.links.map((link) => (
                            <div
                                key={`${section.providerLabel}-${link.key}`}
                                className="flex items-center gap-2 rounded-lg px-1 py-0.5 hover:bg-sky-50 transition-colors"
                            >
                                <button
                                    type="button"
                                    onClick={() => toggleFavorite(link.favoriteKey)}
                                    className={`inline-flex items-center justify-center rounded-md border px-1.5 py-1 flex-shrink-0 transition-colors ${link.isFavorite ? 'border-amber-300 bg-amber-50 text-amber-600 hover:bg-amber-100' : 'border-slate-200 bg-white text-slate-400 hover:bg-slate-50 hover:text-amber-500'}`}
                                    title={link.isFavorite ? 'Unpin favorite destination' : 'Pin favorite destination'}
                                    aria-pressed={link.isFavorite}
                                >
                                    <Star className={`w-3.5 h-3.5 ${link.isFavorite ? 'fill-current' : ''}`} />
                                </button>
                                <a
                                    href={link.url}
                                    target={/^(file:\/\/\/|ms-explorer:)/i.test(String(link.url || '')) ? undefined : '_blank'}
                                    rel={/^(file:\/\/\/|ms-explorer:)/i.test(String(link.url || '')) ? undefined : 'noreferrer'}
                                    className="flex min-w-0 flex-1 items-center justify-between gap-3 rounded-lg px-1.5 py-1.5 text-xs text-slate-700 hover:text-sky-800 transition-colors"
                                    onClick={(event) => {
                                        if (/^(file:\/\/\/|ms-explorer:)/i.test(String(link.url || ''))) {
                                            handleLocalLinkClick(event, section, link);
                                            return;
                                        }

                                        handleCloseMenu();
                                    }}
                                >
                                    <span className="font-medium truncate flex items-center gap-1.5">
                                        <span className="truncate">{link.label}</span>
                                        {link.isFavorite && <span className="text-[9px] uppercase tracking-wide text-amber-600 font-bold">Pinned</span>}
                                    </span>
                                    <ExternalLink className="w-3.5 h-3.5 flex-shrink-0 text-slate-400" />
                                </a>
                                <button
                                    type="button"
                                    onClick={() => handleCopyLink(section, link)}
                                    className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-[10px] font-semibold text-slate-600 hover:bg-slate-50 hover:text-slate-800 transition-colors flex-shrink-0"
                                    title={/^(file:\/\/\/|ms-explorer:)/i.test(String(link.url || '')) ? `Copy ${link.label} path` : `Copy ${link.label} link`}
                                >
                                    {copiedLinkKey === `${section.providerLabel || section.sectionLabel || 'section'}-${link.key}`
                                        ? <Check className="w-3 h-3 text-emerald-600" />
                                        : <Copy className="w-3 h-3" />}
                                    <span>
                                        {copiedLinkKey === `${section.providerLabel || section.sectionLabel || 'section'}-${link.key}`
                                            ? 'Copied'
                                            : (/^(file:\/\/\/|ms-explorer:)/i.test(String(link.url || '')) ? 'Copy Path' : 'Copy Link')}
                                    </span>
                                </button>
                            </div>
                            ))}
                        </div>
                    </div>
                </details>
            );})}
        </div>
    );

    return (
        <div ref={containerRef} className={`relative ${className}`.trim()}>
            <button
                ref={triggerRef}
                type="button"
                onClick={(event) => {
                    event.stopPropagation();
                    setIsOpen((prev) => !prev);
                }}
                className={`inline-flex items-center gap-1.5 rounded-md border border-sky-200 bg-sky-50 text-sky-700 hover:bg-sky-100 transition-colors ${compact ? 'px-2 py-1 text-[11px] font-semibold' : 'px-2.5 py-1.5 text-xs font-semibold'}`}
                title={`Open ${totalLinks} quick link${totalLinks === 1 ? '' : 's'}`}
            >
                <Link2 className={compact ? 'w-3.5 h-3.5' : 'w-4 h-4'} />
                <span>Quick Links</span>
            </button>
            {isOpen && createPortal(menuContent, document.body)}
        </div>
    );
};

export default ExternalQuickLinksButton;